<template>
	<div
		class="organization-release-note-animation"
		:class="[
			{ 'organization-release-note-animation--step-1' : step === 1 },
			{ 'organization-release-note-animation--step-2' : step > 1 },
			{ 'organization-release-note-animation--step-3' : step === 3 },
			{ 'organization-release-note-animation--step-4' : step === 4 },
			{ 'organization-release-note-animation--step-5' : step === 5 },
			{ 'organization-release-note-animation--step-6' : step === 6 },
			{ 'organization-release-note-animation--step-7' : step > 6 },
		]">
		<div class="illu-usergroup"/>

		<div
			v-for="i in 6"
			:key="`${i}-company`"
			class="illu"
			:class="[
				`illu--${i}`,
				{ 'illu--step-1': step === 1 },
				{ 'illu--step-2': step > 1 },
			]">
			<o-icon font-icon="icon-interface-home-2"/>
		</div>

		<div
			v-for="i in 9"
			:key="`${i}-user`"
			class="user"
			:class="[
				`user--${i}`,
				{ 'user--step-1': step === 1 },
				{ 'user--step-2': step > 1 },
			]"/>

		<div
			v-for="i in 2"
			:key="`${i}-role-section`"
			class="role-section"
			:class="{ 'role-section--2' : i === 2 }">
			<div class="role">rôle {{ i === 1 ? 'A' : 'B' }}</div>
			<div
				v-for="j in 3"
				:key="`${j}-permissions`"
				class="permissions">
				<div class="content"/>
				<div
					class="toggle"
					:class="{ 'toggle--active' : i ===1 && (j === 1 || j === 3) || i === 2 && (j === 2 || j === 3) }"
					@click="setup.toggleActive($event)"/>
			</div>
		</div>

		<div
			v-for="i in 6"
			:key="`${i}-doc`"
			class="documents"
			:class="[
				`documents--${i}`,
				{ 'documents--invisible': i === 4 || i === 5 },
			]">
			<div
				v-if="i !== 4 && i !== 5"
				class="tag"/>
			<o-icon
				v-if="i === 4 || i === 5"
				font-icon="icon-interface-edit-view"/>
		</div>

		<div class="user-tag"/>
	</div>
</template>

<script setup lang="ts">
import OrganizationReleaseNoteAnimationSetupService from '@/setup/organization/OrganizationReleaseNoteAnimationSetupService';
const props = defineProps(OrganizationReleaseNoteAnimationSetupService.props);
const setup = new OrganizationReleaseNoteAnimationSetupService(props);
</script>

<style scoped lang="less">
.organization-release-note-animation {
  overflow: hidden;
  position: relative;
  flex: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  height: 300px;
  transition: transform .5s;

  &--step-1 {
    .illu-usergroup {
      opacity: 0;
    }
  }

  &--step-2 {
    .illu-usergroup {
      top: 20px;
      opacity: 1;
    }

    .illu {
      each(range(6), {
        &--@{value} {
          left: calc(@value * 60px - 30px);
        }
      })
    }

    .user {
      &--1 {
        top: 120px;
        left: 43px;
      }
      &--2 {
        top: 170px;
        left: 43px;
      }
      &--3 {
        top: 120px;
        left: 103px;
      }
      &--4 {
        top: 170px;
        left: 103px;
      }
      &--5 {
        top: 120px;
        left: 163px;
      }
      &--6 {
        top: 120px;
        left: 223px;
      }
      &--7 {
        top: 170px;
        left: 223px;
      }
      &--8 {
        top: 120px;
        left: 283px;
      }
      &--9{
        top: 120px;
        left: 343px;
      }
    }

    .role-section, .role-section--2 {
      transition-delay: 0;
    }
  }

  &--step-3 {
    .user-tag {
      transition: unset;
      opacity: 0;
    }

    .illu-usergroup, .illu {
      transition: all .5s;
      top: -100px;
      opacity: 0;
    }

    .role-section{
      transition: opacity .7s, top .4s;
      top: 180px;
      opacity: 1;

      &--2 {
        transition-delay: 150ms;
      }
    }

    .user {
      transition: all .5s;
      top: 60px;
    }

    .user--2, .user--4, .user--7 {
      top: 110px;
    }

    .user--1, .user--4, .user--5, .user--6 {
      background: var(--warning-alt);
    }

    .user--2, .user--3, .user--7, .user--8, .user--9 {
      background: var(--danger-alt);
    }
  }

  &--step-4 {
    div:not(.user--1):not(.documents):not(.tag):not(.user-tag){
      transition: all .4s;
      opacity: 0;
    }

    .tag {
      opacity: 1;
      bottom: 5px;
      transform: scale(1);
    }

    .user-tag {
      transition-delay: 1000ms;
      opacity: 1;
      top: 60px;
      left: 205px;
    }

    .documents {
      transition: left .4s, top .4s, opacity .5s;
      top: 140px;
      opacity: 1;

      each(range(6), {
        &--@{value} {
          transition-delay: calc(@value * 100ms);
          left: calc(60px * (@value) - 30px );

          .orion-icon {
            opacity: 1;
            transition: opacity .3s;
            transition-delay: calc(@value * 100ms + 700ms);
          }

          .tag {
            transition-delay: calc(@value * 80ms + 700ms);;
          }

          &::before {
            opacity: .8;
            transition-delay: calc(@value * 100ms + 700ms);
          }
        }
      })
    }

    .user--1 {
      transition: all .7s;
      transition-delay: 400ms;
      top: 40px;
      left: 193px;
      scale: 1.4;
    }
  }

  &--step-5 {
    div:not(.illu) {
      display: none;
    }

    .illu {
      transition: all 1s ease-in-out;
      animation: 1s alternate-reverse infinite loading;

      .orion-icon {
        display: none;
      }

      each(range(6), {
        &--@{value} {
          animation-delay: calc(@value * 100ms + 1000ms);
          transition-delay: calc(@value * 160ms + 1000ms);
        }
      })

      &--1 {
        top: 80px;
        left: 120px;
      }
      &--6 {
        top: 120px;
        left: 280px;
      }
      &--5 {
        top: 230px;
        left: 320px;
      }
      &--4 {
        top: 370px;
        left: 240px;
      }
      &--3 {
        top: 350px;
        left: 100px;
      }
      &--2 {
        top: 210px;
        left: 30px;
      }
    }
  }

  &--step-6, &--step-7 {
    display: none;
  }
}

.illu-usergroup {
  transition: opacity .4s, top .7s ease-in-out;
  position: absolute;
  top: -100px;
  width: 100%;
  height: 80px;
  border-radius: 10px;
  background: var(--grey-lighter);
}

.illu {
  height: 40px;
  width: 40px;
  position: absolute;
  background: var(--grey-light);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
  color: var(--grey-dark);
  border-radius: 5px;
  transition: all .7s;

  each(range(6), {
    &--@{value} {
      transition-delay: calc(@value * 0.02s) !important;
    }
  });

  &--1 {
    top: 170px;
    left: 30px;
  }
  &--2 {
    top: 220px;
    left: 140px;
  }
  &--3 {
    top: 50px;
    left: 100px;
  }
  &--4 {
    top: 110px;
    left: 230px;
  }
  &--5 {
    top: 200px;
    left: 320px;
  }
  &--6 {
    top: 20px;
    left: 290px;
  }

  &--step-2 {
    top: 40px;
  }
}

.user {
  transition: top .7s, left .7s, background .3s;
  position: absolute;
  height: 13px;
  width: 13px;
  background: var(--grey-light);
  border-radius: 50%;

  each(range(9), {
    &--@{value} {
      transition-delay: calc(@value * 0.02s) !important;
    }
  });

  &--1{
    top: 15px;
    left: 45px;
  }
  &--2{
    top: 30px;
    left: 200px;
  }
  &--3{
    top: 90px;
    left: 70px;
  }
  &--4{
    top: 230px;
    left: 20px;
  }
  &--5{
    top: 120px;
    left: 350px;
  }
  &--6{
    top: 150px;
    left: 130px;
  }
  &--7{
    top: 90px;
    left: 300px;
  }
  &--8{
    top: 200px;
    left: 270px;
  }
  &--9{
    top: 180px;
    left: 200px;
  }

  &::after {
    content: "";
    position: absolute;
    left: -4px;
    bottom: -17px;
    height: 15px;
    width: 21px;
    border-radius: 10px 10px 3px 3px;
    background: inherit;
  }
}

.role-section {
  position: absolute;
  left: 20px;
  width: calc(50% - 30px);
  top: 210px;
  opacity: 0;
  transition: all .2s;

  &--2 {
    left: 220px;
    .role, .toggle--active {
      background: var(--danger-alt);
    }
  }
}

.role {
  width: 50px;
  height: 20px;
  background-color: var(--warning-alt);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 10px;
}

.permissions {
  display: flex;
  gap: 6px;

  + .permissions {
    margin-top: 10px;
  }
}

.toggle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  height: 10px;
  background: var(--grey-light);
  border-radius: 5px;
  padding: 2px;

  &::after {
    z-index: 2;
    background: white;
    content: "";
    height: 6px;
    width: 6px;
    border-radius: 50%;
    position: absolute;
  }

  &--active {
    justify-content: flex-end;
    background: var(--warning-alt);
  }
}

.documents {
  opacity: 0;
  position: absolute;
  height: 60px;
  width: 40px;
  top: 190px;
  background-color: var(--grey-light);
  border-radius: 5px;
  color: var(--grey-dark);
  display: flex;
  align-items: center;
  font-size: 18px;
  justify-content: center;

  &--invisible {
    &::before {
      content: "";
      position: absolute;
      height: 100%;
      width: 100%;
      background: white;
      opacity: 0;
      transition: opacity .4s;
    }
  }

  &::after {
    content: "";
    height: 20px;
    width: 20px;
    position: absolute;
    transform: rotate(45deg);
    background: white;
    top: -12px;
    right: -12px;
  }

  .arm-icon {
    opacity: 0;
  }
}

.tag {
  opacity: 0;
  display: inline;
  position: absolute;
  bottom: -10px;
  color: white;
  padding: 4px 10px;
  background: var(--info);
  transform: scale(.4);
  clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 25% 100%, 0% 50%);
  transition: opacity .4s, transform .4s cubic-bezier(0.175, 0.885, 0.32, 1.5), bottom .4s;
}

.user-tag {
  position: absolute;
  padding: 5px 12px;
  background: var(--info);
  opacity: 0;
  top: 100px;
  left: 185px;
  transition: opacity .4s, top .4s;
  clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 25% 100%, 0% 50%);
}

.content {
  flex: 9;
  height: 10px;
  border-radius: 5px;
  background: var(--grey-light);
}

@keyframes loading {
  0% {
    transform: scale(.9);
    opacity: .3;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
